import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, MapPin, Zap, Layers, Package, Clock, CheckCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = "http://127.0.0.1:8000";
const API = `${BACKEND_URL}/api/v1`;

export const HomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, hasFullAccess } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const scrollToUpload = () => {
    document
      .getElementById("upload-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/" } });
      return;
    }
    if (
      !file.name.toLowerCase().endsWith(".stl") &&
      !file.name.toLowerCase().endsWith(".obj")
    ) {
      toast.error("Only STL and OBJ files are supported");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File size exceeds 50MB limit");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded successfully!");
      navigate(`/viewer/${response.data.file_id}`);
    } catch (error) {
      console.error("Upload error:", error);
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((e) => `${e.loc?.at(-1) ?? "field"}: ${e.msg}`).join(", ") : detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen">

      {/* ── HERO BANNER ── */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/page1.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
      </section>

      {/* ── UPLOAD ── */}
      <section id="upload-section" className="py-20 px-6 bg-secondary">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl font-bold mb-3">
              Upload Your 3D Model
            </h2>
            <p className="text-muted-foreground text-sm">
              STL or OBJ format · Max 50MB · Instant geometry analysis
            </p>
          </div>

          {/* Access gate banners */}
          {isAuthenticated && !user?.is_verified && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-5 mb-6 flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800 mb-1">Please verify your email</p>
                <p className="text-amber-700">Check your inbox for the verification link we sent when you registered.</p>
              </div>
            </div>
          )}
          {isAuthenticated && user?.is_verified && user?.account_status === "pending" && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-5 mb-6 flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-blue-800 mb-1">Account under review</p>
                <p className="text-blue-700">Our team is reviewing your business application. You'll receive an email once approved.</p>
              </div>
            </div>
          )}
          {isAuthenticated && user?.account_status === "rejected" && (
            <div className="bg-red-50 border border-red-200 rounded-md p-5 mb-6 flex items-start gap-3">
              <Clock className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-red-800 mb-1">Account application not approved</p>
                <p className="text-red-700">Contact <a href="mailto:support@kastiq.com" className="underline">support@kastiq.com</a> for more information.</p>
              </div>
            </div>
          )}
          {isAuthenticated && hasFullAccess && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-6 flex items-center gap-2 text-sm text-green-800">
              <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
              Account approved — you have full access.
            </div>
          )}

          <div
            className={`bg-white rounded-md border-2 border-dashed p-16 transition-all duration-300 cursor-pointer ${
              dragActive
                ? "border-accent bg-accent-light"
                : "border-border hover:border-accent/50"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => {
              if (!isAuthenticated) { navigate("/login", { state: { from: "/" } }); return; }
              fileInputRef.current?.click();
            }}
            data-testid="upload-dropzone"
          >
            <div className="text-center">
              <div
                className={`w-16 h-16 rounded-full border-2 border-dashed flex items-center justify-center mx-auto mb-6 transition-colors ${
                  dragActive ? "border-accent bg-accent-light" : "border-accent"
                }`}
              >
                <Upload className="w-7 h-7 text-accent" />
              </div>
              <h3 className="font-sans font-semibold text-xl mb-2">
                Drop your file here
              </h3>
              <p className="text-muted-foreground text-sm mb-6">
                or click anywhere in this box to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                id="file-upload"
                className="hidden"
                accept=".stl,.obj"
                onChange={handleFileInput}
                disabled={uploading}
              />
              <Button
                className="bg-accent hover:bg-accent-hover text-white px-8"
                disabled={uploading}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAuthenticated) { navigate("/login", { state: { from: "/" } }); return; }
                  fileInputRef.current?.click();
                }}
                data-testid="upload-browse-button"
              >
                {uploading ? "Uploading..." : "Choose File"}
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Maximum file size: 50MB
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              From your file to your finger —<br className="hidden md:block" />
              in three steps
            </h2>
            <p className="text-muted-foreground text-base">
              Every Kastiq piece is born the same way: a file, a cast, a
              creation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10 mb-14">
            {/* Step 1: Upload */}
            <div className="text-center">
              <div className="w-28 h-28 rounded-full border-2 border-accent flex items-center justify-center mx-auto mb-6">
                <span className="font-sans font-bold text-lg tracking-[0.15em] text-accent">
                  STL
                </span>
              </div>
              <p className="text-[11px] font-bold tracking-[0.3em] text-accent mb-3 uppercase">
                Step 01
              </p>
              <h3 className="font-heading font-bold text-xl mb-3">
                Upload Your 3D File
              </h3>
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                Upload your STL or OBJ file. Our system instantly analyzes
                geometry, volume, and raises any manufacturing warnings.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="border-accent text-accent hover:bg-accent-light"
                onClick={scrollToUpload}
              >
                Upload File →
              </Button>
            </div>

            {/* Step 2: Configure */}
            <div className="text-center">
              <div className="w-28 h-28 rounded-full border-2 border-cast flex items-center justify-center mx-auto mb-6">
                <span className="font-sans font-bold text-lg tracking-[0.15em] text-cast">
                  CAST
                </span>
              </div>
              <p className="text-[11px] font-bold tracking-[0.3em] text-cast mb-3 uppercase">
                Step 02
              </p>
              <h3 className="font-heading font-bold text-xl mb-3">
                Pick Your Metal
              </h3>
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                Choose from 13 precious metals. See real-time pricing across
                platinum, gold alloys, and sterling silver instantly.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="border-cast text-cast hover:bg-cast-light"
                onClick={scrollToUpload}
              >
                Get Casting Quote →
              </Button>
            </div>

            {/* Step 3: Order */}
            <div className="text-center">
              <div className="w-28 h-28 rounded-full border-2 border-craft flex items-center justify-center mx-auto mb-6">
                <span className="font-sans font-bold text-lg tracking-[0.12em] text-craft">
                  ORDER
                </span>
              </div>
              <p className="text-[11px] font-bold tracking-[0.3em] text-craft mb-3 uppercase">
                Step 03
              </p>
              <h3 className="font-heading font-bold text-xl mb-3">
                Receive Your Piece
              </h3>
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                We precision cast using lost-wax techniques, finish your piece,
                and deliver it directly to you.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="border-craft text-craft hover:bg-craft-light"
                onClick={() => navigate("/cart")}
              >
                View Cart →
              </Button>
            </div>
          </div>

          {/* Callout */}
          <p className="text-center text-base mb-8">
            Instant STL analysis is{" "}
            <span className="font-bold text-accent">FREE</span> — just upload
            your model.
          </p>

          <div className="text-center">
            <Button
              onClick={scrollToUpload}
              className="bg-accent hover:bg-accent-hover text-white px-10 py-6 text-sm tracking-wider"
            >
              Start Your Custom Piece →
            </Button>
          </div>
        </div>
      </section>

      {/* ── FEATURES GRID ── */}
      <section className="py-16 px-6 bg-white border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {
                icon: <MapPin className="w-5 h-5" />,
                title: "Los Angeles, CA",
                desc: "Based in the heart of the Jewelry District.",
              },
              {
                icon: <Zap className="w-5 h-5" />,
                title: "Instant Pricing",
                desc: "Real-time calculation as you configure.",
              },
              {
                icon: <Layers className="w-5 h-5" />,
                title: "13 Metal Options",
                desc: "From platinum to sterling silver.",
              },
              {
                icon: <Package className="w-5 h-5" />,
                title: "Track Everything",
                desc: "See exactly where your piece is, anytime.",
              },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                className="p-6 border border-border rounded-md hover:border-accent/40 transition-colors"
              >
                <div className="text-muted-foreground mb-3">{icon}</div>
                <h3 className="font-sans font-semibold text-sm mb-1.5">
                  {title}
                </h3>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GREEN CTA BANNER ── */}
      <section className="py-24 px-6 bg-accent text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-8">
            Ready to bring your design to life?
          </h2>
          <Button
            onClick={scrollToUpload}
            className="bg-white text-accent hover:bg-accent-light px-10 py-6 text-sm tracking-wider mb-5"
          >
            Start a Custom Order →
          </Button>
          <p className="text-white/70 text-sm mt-4">
            Or call us at{" "}
            <span className="text-white font-semibold">+1 (555) 123-4567</span>
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
